import { useState, useEffect, useRef } from "react";
import arrayUtilities from "../../utilities/arrayUtilities";
import stringHelpers from "../../utilities/stringHelpers";
import Api from "../api/api";
import { ReportsCharts } from "./reportsCharts";
import { dateRange, toDateString } from "../../utilities/dateUtilities";
import { reportDatePresetByLabel } from "./reportsEnums";
import { saveAs } from 'file-saver';
import { EmptyState } from "../dashboard/emptyState";
import Loader from "../shared/loader";
import { Link } from "react-router-dom/cjs/react-router-dom";
import { FiltersModalTrigger } from "../shared/filtersModalTrigger";
import { AppliedFilterPill, renderTagFilterItemLabel } from "../shared/appliedFilterPill";
import appliedFiltersUtilities from "../../utilities/appliedFiltersUtilities";
import { DebtRecoveryReportFiltersModal } from "./filters-modals/debtRecoveryReportFiltersModal";
import { DebtRecoveryScheduleModal } from "./schedule-modals/debtRecoveryScheduleModal";
import { downloadReportPdf } from "./downloadReportPdf";
import { PrintPdfHeader } from "./printPdfHeader";
const groupByLabel = (value) => value === 1 ? "Tag" : "Site"

export const DebtRecoveryReport = ({ sites, clients, tags, organisationId, token, organisationName, organisationLogo }) => {

    const [reportData, setReportData] = useState(null);
    const [fromDate, setFromDate] = useState(dateRange("lastMonth").firstDate);
    const [toDate, setToDate] = useState(dateRange("lastMonth").lastDate);
    const [datePreset, setDatePreset] = useState(reportDatePresetByLabel["Last month"]);
    const [selectedClient, setSelectedClient] = useState("");
    const [sitesByClient, setSitesByClient] = useState(sites)
    const [selectedSiteByClient, setSelectedSiteByClient] = useState("")
    const [debtRecoveryGroupBy, setDebtRecoveryGroupBy] = useState(0)
    const [selectedTags, setSelectedTags] = useState([])
    const [isLoading, setIsLoading] = useState(true);
    const [showFiltersModal, setShowFiltersModal] = useState(false);
    const [showFilterPills, setShowFilterPills] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState(null);
    const reportsLandingUrl = `/organisation/${ organisationId }/reports`;

    const [showScheduleExportModal, setShowScheduleExportModal] = useState(false);
    const pdfRef = useRef()

    const pdfClientName = (clientId) => {
        if (!stringHelpers.isNullOrEmpty(clientId)) {
            const client = clients.find(item => item.id === clientId)
            if (client !== undefined) {
                return client.name
            }
        }
        return "All"
    }

    const pdfSiteName = (siteId) => {
        if (!stringHelpers.isNullOrEmpty(siteId)) {
            const site = sitesByClient.find(item => item.id === siteId)
            if (site !== undefined) {
                return site.name
            }
        }
        return "All"
    }

    const handleResetFilters = () => {
        const defaultRange = dateRange("lastMonth");
        setFromDate(defaultRange.firstDate);
        setToDate(defaultRange.lastDate);
        setDatePreset(reportDatePresetByLabel["Last month"]);
        setSelectedClient("");
        setSitesByClient(sites)
        setSelectedSiteByClient("")
        setDebtRecoveryGroupBy(0)
        setSelectedTags([])
        setAppliedFilters({
            "Date Range": `${ toDateString(new Date(defaultRange.firstDate)) } - ${ toDateString(new Date(defaultRange.lastDate)) }`,
            "Group By": "Site"
        });
        setShowFilterPills(true);
        fetchDebtRecoveryReport(defaultRange.firstDate, defaultRange.lastDate, "", "", 0, []);
    }

    const clearFilterByLabel = (label) => {
        switch (label) {
            case "Client":
                setSelectedClient("")
                setSelectedSiteByClient("")
                setSitesByClient(sites)
                fetchDebtRecoveryReport(fromDate, toDate, "", "", debtRecoveryGroupBy, selectedTags)
                break
            case "Site":
                setSelectedSiteByClient("")
                fetchDebtRecoveryReport(fromDate, toDate, "", selectedClient, debtRecoveryGroupBy, selectedTags)
                break
            case "Date Range": {
                const defaultRange = dateRange("lastMonth")
                setFromDate(defaultRange.firstDate)
                setToDate(defaultRange.lastDate)
                setDatePreset(reportDatePresetByLabel["Last month"])
                fetchDebtRecoveryReport(defaultRange.firstDate, defaultRange.lastDate, selectedSiteByClient, selectedClient, debtRecoveryGroupBy, selectedTags)
                break
            }
            case "Group By":
                setDebtRecoveryGroupBy(0)
                fetchDebtRecoveryReport(fromDate, toDate, selectedSiteByClient, selectedClient, 0, selectedTags)
                break
            case "Tags":
                setSelectedTags([])
                fetchDebtRecoveryReport(fromDate, toDate, selectedSiteByClient, selectedClient, debtRecoveryGroupBy, [])
                break
            default:
                break
        }
    }

    const handleFilterPillClose = closedLabel => {
        clearFilterByLabel(closedLabel)
        setAppliedFilters(prevState => {
            const updatedFilters = { ...prevState }
            if (closedLabel === "Date Range") {
                const defaultRange = dateRange("lastMonth")
                updatedFilters["Date Range"] = `${ toDateString(new Date(defaultRange.firstDate)) } - ${ toDateString(new Date(defaultRange.lastDate)) }`
            } else if (closedLabel === "Client") {
                updatedFilters.Client = null
                updatedFilters.Site = null
            } else if (closedLabel === "Group By") {
                updatedFilters["Group By"] = "Site"
            } else {
                updatedFilters[closedLabel] = null
            }
            return appliedFiltersUtilities.hasRemainingFilters(updatedFilters) ? updatedFilters : null
        })
    }

    const handleFilterPillItemRemove = (label, remainingIds) => {
        setAppliedFilters(prevState => appliedFiltersUtilities.keepMultiSelectionItems(prevState, label, remainingIds))
        removeMultiSelectFilterItemByLabel(label, remainingIds)
    }

    const removeMultiSelectFilterItemByLabel = (label, remainingIds) => {
        switch (label) {
            case "Tags":
                setSelectedTags(remainingIds)
                fetchDebtRecoveryReport(fromDate, toDate, selectedSiteByClient, selectedClient, debtRecoveryGroupBy, remainingIds)
                break
            default:
                break
        }
    }

    const fetchDebtRecoveryReport = (from, to, siteId, clientId, groupBy, tagIds) => {
        setIsLoading(true)
        Api.getDebtRecoveryReport(
            reportData => {
                setReportData(reportData.map(item => ({ label: groupBy === 1 ? item.tagName : item.siteName, value: item.breachesSent })));
                setIsLoading(false);
            },
            error => {
                console.log(error)
                setIsLoading(false)
            },
            from, to, 0, !stringHelpers.isNullOrEmpty(siteId) ? siteId : null,
            !stringHelpers.isNullOrEmpty(clientId) ? clientId : null, groupBy, tagIds, organisationId, token
        )
    }

    const handleDownload = () => {
        Api.getDebtRecoveryReport(
            (blob, filename) => saveAs(blob, filename),
            () => { setIsLoading(false) },
            fromDate,
            toDate,
            1,
            !stringHelpers.isNullOrEmpty(selectedSiteByClient) ? selectedSiteByClient : null,
            !stringHelpers.isNullOrEmpty(selectedClient) ? selectedClient : null,
            debtRecoveryGroupBy,
            selectedTags,
            organisationId,
            token
        )
    }

    const groupingColumnHeading = debtRecoveryGroupBy === 1 ? "Tag" : "Site Name"

    const selectedTagNames = !arrayUtilities.isNullOrEmpty(selectedTags) && !arrayUtilities.isNullOrEmpty(tags)
        ? selectedTags
            .map(selectedTagId => tags.find(tag => tag.id === selectedTagId))
            .filter(tag => tag !== undefined)
            .map(tag => tag.name)
            .join(", ")
        : "All"


    const scheduleDateParameters = datePreset !== null
        ? { reportDatePreset: datePreset }
        : { from: fromDate, to: toDate }

    const scheduleInitialParameters = {
        ...scheduleDateParameters,
        siteId: selectedSiteByClient,
        clientId: selectedClient,
        reportGroupBy: debtRecoveryGroupBy,
        tagIds: selectedTags,
    }

    const closeScheduleExportModal = () => setShowScheduleExportModal(false)
    useEffect(() => {
        fetchDebtRecoveryReport(fromDate, toDate, "", "", debtRecoveryGroupBy, selectedTags);
        setAppliedFilters({
            "Date Range": `${ toDateString(new Date(fromDate)) } - ${ toDateString(new Date(toDate)) }`,
            "Group By": groupByLabel(debtRecoveryGroupBy)
        });
        setShowFilterPills(true)
    }, [])

    return (
        <>
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div>
                                        <h5 className="mb-0">
                                            Debt Recovery
                                        </h5>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <div className="d-flex mr-2 align-items-center">
                                            <FiltersModalTrigger onClick={ () => setShowFiltersModal(true) } />
                                        </div>
                                        <button
                                            onClick={ handleDownload }
                                            className="btn btn-sm btn-default text-white bg-dark nowrap mr-2"
                                        >
                                            Download CSV<i className="ml-2 fas fa-file-excel"></i>
                                        </button>
                                        <button
                                            onClick={ () => downloadReportPdf(pdfRef, `${ organisationName } - Debt Recovery`) }
                                            className="btn btn-sm btn-default nowrap mr-2 text-white" style={ { backgroundColor: "#4A7C8E" } }
                                        >
                                            Download PDF<i className="ml-2 far fa-file-pdf"></i>
                                        </button>
                                        <button
                                            onClick={ () => setShowScheduleExportModal(true) }
                                            className="btn btn-sm btn-secondary nowrap mr-2"
                                        >
                                            Schedule Report<i className="ml-2 far fa-clock"></i>
                                        </button>
                                        <Link
                                            className="btn btn-falcon-default btn-sm mr-3"
                                            type="button"
                                            to={ reportsLandingUrl }>
                                            <i className="fas fa-reply"></i> Back
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            <div className="card-body border-top d-flex flex-column">
                                {
                                    showFilterPills && appliedFilters &&
                                    <div className="d-flex align-items-center flex-wrap">
                                        {
                                            Object.entries(appliedFilters).map(
                                                ([label, value]) => {
                                                    const isResettable = label === "Date Range" || label === "Group By"
                                                    return <AppliedFilterPill
                                                        key={ label }
                                                        label={ label }
                                                        value={ value }
                                                        isResettable={ isResettable }
                                                        renderItemLabel={ renderTagFilterItemLabel }
                                                        onTextClick={ () => setShowFiltersModal(true) }
                                                        onCloseClick={ handleFilterPillClose }
                                                        onRemoveItem={ handleFilterPillItemRemove }
                                                    />
                                                }
                                            )
                                        }
                                        { appliedFilters && Object.keys(appliedFilters).length > 0 && <button type="button" className="btn btn-none p-0 m-0 fs--1 nowrap" onClick={ () => handleResetFilters() }>Reset all filters</button> }
                                    </div>
                                }
                                <div ref={ pdfRef }>
                                    <PrintPdfHeader organisationLogo={ organisationLogo } organisationName={ organisationName } title="Debt Recovery">
                                        <div className="print-only-filter">
                                            <span className="print-only-filter-label">Date Range</span>
                                            <span className="print-only-filter-value">
                                                { toDateString(fromDate) } - { toDateString(toDate) }
                                            </span>
                                        </div>
                                        <div className="print-only-filter">
                                            <span className="print-only-filter-label">Client</span>
                                            <span className="print-only-filter-value">
                                                { pdfClientName(selectedClient) }
                                            </span>
                                        </div>
                                        <div className="print-only-filter">
                                            <span className="print-only-filter-label">Site</span>
                                            <span className="print-only-filter-value">
                                                { pdfSiteName(selectedSiteByClient) }
                                            </span>
                                        </div>
                                        <div className="print-only-filter">
                                            <span className="print-only-filter-label">Group By</span>
                                            <span className="print-only-filter-value">
                                                { groupByLabel(debtRecoveryGroupBy) }
                                            </span>
                                        </div>
                                        <div className="print-only-filter">
                                            <span className="print-only-filter-label">Tags</span>
                                            <span className="print-only-filter-value">
                                                { selectedTagNames }
                                            </span>
                                        </div>
                                    </PrintPdfHeader>
                                {
                                    isLoading
                                        ? <Loader />
                                        : <>
                                            {
                                                !arrayUtilities.isNullOrEmpty(reportData)
                                                    ? (
                                                        <div className="mt-3 d-flex flex-column">
                                                            <ReportsCharts tooltipCustomValueLabel="Breaches sent" data={ reportData } chartStyle="bar" />

                                                            <div className="card-body bg-white border-top p-0">
                                                                <table className="table table-sm mb-0 table-striped table-dashboard fs--1 data-table border-bottom border-200 no-footer ">
                                                                    <thead className="bg-200 text-900">
                                                                        <tr>
                                                                            <th>{ groupingColumnHeading }</th>
                                                                            <th className="text-right">Breaches Sent</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {
                                                                            reportData.map(
                                                                                (item, index) => <tr key={ index }>
                                                                                    <td>{ item.label }</td>
                                                                                    <td className="text-right">{ stringHelpers.formatDecimal(item.value, 0) }</td>
                                                                                </tr>
                                                                            )
                                                                        }
                                                                        <tr className="font-weight-bold w-100">
                                                                            <td>Total</td>
                                                                            <td className="text-right">
                                                                                { stringHelpers.formatDecimal(reportData.reduce((acc, curr) => acc + curr.value, 0), 0) }
                                                                            </td>
                                                                        </tr>
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    ) : <EmptyState message="No Data available for the selected filters" />
                                            }
                                        </>
                                }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {
                showFiltersModal &&
                <DebtRecoveryReportFiltersModal
                    showModal={ showFiltersModal }
                    handleClose={ () => setShowFiltersModal(false) }
                    handleConfirm={ (draftFilters) => {
                        setFromDate(draftFilters.fromDate)
                        setToDate(draftFilters.toDate)
                        setDatePreset(draftFilters.datePreset)
                        setSelectedClient(draftFilters.selectedClient)
                        setSitesByClient(draftFilters.sitesByClient)
                        setSelectedSiteByClient(draftFilters.selectedSiteByClient)
                        setDebtRecoveryGroupBy(draftFilters.debtRecoveryGroupBy)
                        setSelectedTags(draftFilters.selectedTags)
                        setAppliedFilters(draftFilters.appliedFilters)
                        setShowFilterPills(true)
                        setShowFiltersModal(false)
                        fetchDebtRecoveryReport(draftFilters.fromDate, draftFilters.toDate, draftFilters.selectedSiteByClient, draftFilters.selectedClient, draftFilters.debtRecoveryGroupBy, draftFilters.selectedTags)
                    } }
                    initialFromDate={ fromDate }
                    initialToDate={ toDate }
                    initialDatePreset={ datePreset }
                    initialSelectedClient={ selectedClient }
                    initialSelectedSiteByClient={ selectedSiteByClient }
                    initialSitesByClient={ sitesByClient }
                    initialDebtRecoveryGroupBy={ debtRecoveryGroupBy }
                    initialSelectedTags={ selectedTags }
                    initialAppliedFilters={ appliedFilters }
                    clients={ clients }
                    sites={ sites }
                    tags={ tags || [] }
                    organisationId={ organisationId }
                    token={ token }
                    key="debt-recovery-report-filters-modal"
                />
            }
            {
                showScheduleExportModal &&
                <DebtRecoveryScheduleModal
                    sites={ sites }
                    clients={ clients }
                    tags={ tags || [] }
                    organisationId={ organisationId }
                    token={ token }
                    initialParameters={ scheduleInitialParameters }
                    initialSitesByClient={ sitesByClient }
                    onCancel={ closeScheduleExportModal }
                    onSaved={ closeScheduleExportModal }
                />
            }
        </>
    )
}

